/**
 * Error reporting for Game Lite.
 *
 * Until now this app had none — zero Sentry imports against apps/game's 65 —
 * so every failure it was carefully built to survive (a dropped write, a
 * refused token, the reveal watchdog firing) went to `console.error` on a
 * stranger's phone in a venue and was never seen again. The durable queue can
 * tell you an answer was abandoned; nobody was listening.
 *
 * Deliberately lighter than apps/game's `utils/sentry.js`. Lite's bundle is the
 * reason the app exists, so this takes no tracing, no replay and no console
 * integration — only explicit captures from the handful of places that can
 * lose a player's work. The three that matter are tagged so they can be
 * alerted on directly: `lite.write_dropped`, `lite.write_expired`,
 * `lite.token_refused`.
 *
 * And it is fetched ON THE FIRST REPORT, not at startup. Imported eagerly,
 * @sentry/react put 28 kB gzipped into the entry chunk of an app whose small
 * bundle is its whole reason for existing. Split out but still loaded on boot,
 * it was a 141 kB download on every session — paid by every player, on venue
 * wifi, to report a failure most of them will never have. So nothing is
 * fetched until something actually goes wrong: a clean tasting costs nothing
 * at all, and the session that does hit trouble pays once.
 *
 * Reports raised before the SDK lands are buffered (BUFFER_LIMIT of them) and
 * sent when it arrives, so the first error — the one that triggered the load,
 * and usually the interesting one — is never the one that gets lost. Every
 * report is consoled immediately either way, so a device that can't fetch the
 * chunk at all still says so in its own logs.
 */

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0', '[::1]']);

// apps/game's DSN. Lite reports into the same Sentry project, told apart by the
// `app` tag below — the tag apps/game already sets for exactly this reason.
// Set VITE_SENTRY_DSN to send Lite somewhere else instead.
const DEFAULT_DSN =
  'https://26b2a44ac7698f3de200bb8b65d5b82a@o4510306005680128.ingest.de.sentry.io/4510306016624720';

/**
 * Where this bundle is RUNNING, not how it was built: `vite build && vite
 * preview` on a laptop reports MODE=production, which is how apps/game once
 * logged 36 local events tagged `production` (TAS-324).
 */
export function resolveEnvironment() {
  const explicit = import.meta.env.VITE_SENTRY_ENV;
  if (explicit) return explicit;
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  if (LOCAL_HOSTS.has(host) || /^(10|127|192\.168)\./.test(host) || host.endsWith('.local')) {
    return 'local';
  }
  if (host.endsWith('.vercel.app')) return 'preview';
  if (host) return 'production';
  return import.meta.env.MODE || 'development';
}

// Set once the SDK has landed; everything before that is buffered.
let Sentry = null;
let started = false;
let loading = null;
const BUFFER_LIMIT = 20;
const buffered = [];
let identity = null;

function flushBuffer() {
  while (buffered.length > 0) {
    const entry = buffered.shift();
    try {
      entry();
    } catch {
      /* one bad buffered report must not stop the rest */
    }
  }
}

/** Queue a report until the SDK is here. Oldest is dropped past the limit. */
function whenReady(fn) {
  if (started && Sentry) {
    try {
      fn();
    } catch {
      /* reporting must never be the thing that breaks a tasting */
    }
    return;
  }
  if (buffered.length >= BUFFER_LIMIT) buffered.shift();
  buffered.push(fn);
}

let armed = false;

/**
 * Arm reporting. Cheap and synchronous — it resolves the DSN and nothing more.
 * The SDK itself is fetched by the first report that needs it (see
 * `ensureLoaded`), so a session that never fails never downloads it.
 */
export function initSentry() {
  armed = Boolean(import.meta.env.VITE_SENTRY_DSN || DEFAULT_DSN);
  return armed;
}

/**
 * Fetch and start the SDK. Safe to call more than once; returns the same
 * promise. Resolves false when reporting was never armed, or the chunk cannot
 * be fetched (an offline session) — in which case reports stay in the console.
 */
export function ensureLoaded() {
  if (loading) return loading;
  const dsn = import.meta.env.VITE_SENTRY_DSN || DEFAULT_DSN;
  if (!armed || !dsn) return Promise.resolve(false);

  loading = import('@sentry/react')
    .then((mod) => {
      Sentry = mod;
      Sentry.init({
        dsn,
        environment: resolveEnvironment(),
        // No tracing, no replay, no console capture: this app is opened on
        // budget phones inside webviews and the point of it is that it is small.
        tracesSampleRate: 0,
        // An email address is the player's identity here (solo_join keys on
        // it), so it must not be swept up automatically.
        sendDefaultPii: false,
        initialScope: { tags: { app: 'game-lite' } },
      });
      started = true;
      if (identity) applyIdentity(identity);
      flushBuffer();
      return true;
    })
    .catch(() => {
      // Nothing to do but keep consoling. Don't retry: a device that can't
      // fetch this chunk has bigger problems than telemetry.
      loading = Promise.resolve(false);
      return false;
    });

  return loading;
}

/** True once init has run — so a report can no-op quietly rather than throw. */
export const sentryStarted = () => started;

/** Test seam — forget the SDK between cases. */
export function __resetSentryForTests() {
  Sentry = null;
  started = false;
  loading = null;
  identity = null;
  armed = false;
  buffered.length = 0;
}

/**
 * Report something that lost, or nearly lost, a player's work.
 *
 * Supabase hands back plain objects ({ code, details, hint, message }), which
 * Sentry files as "Object captured as exception" with nothing useful in it, so
 * anything that isn't an Error is wrapped before it goes.
 */
export function reportLoss(what, err, context = {}) {
  // Always, immediately, whether or not the SDK is up.
  console.error(`[lite] ${what}`, err, context);

  // Read the error NOW: by the time the buffer flushes, the object it came
  // from may well have been mutated or reused.
  const message =
    typeof err?.message === 'string' && err.message.trim() ? err.message : String(what);
  const wrapped = err instanceof Error ? err : new Error(`${what}: ${message}`);
  const supabaseFields =
    err && !(err instanceof Error)
      ? { code: err.code, details: err.details, hint: err.hint, message: err.message }
      : undefined;

  whenReady(() => {
    Sentry.captureException(wrapped, {
      tags: { app: 'game-lite', lite_event: what },
      extra: { supabase: supabaseFields, ...context },
    });
  });
  // A real failure is what earns the download. Breadcrumbs and identity do
  // not: they only matter once something has gone wrong, and by then this has
  // already been called.
  ensureLoaded();
}

/** A breadcrumb for something notable that is not yet a failure. */
export function noteEvent(what, context = {}) {
  whenReady(() => {
    Sentry.addBreadcrumb({ category: 'lite', message: what, level: 'info', data: context });
  });
}

function applyIdentity({ participantId, sessionCode }) {
  Sentry.setUser(participantId ? { id: participantId } : null);
  Sentry.setTag('session_code', sessionCode || undefined);
}

/** So a report says which participant and tasting it belongs to. */
export function setSentryParticipant({ participantId, sessionCode } = {}) {
  identity = { participantId, sessionCode };
  whenReady(() => applyIdentity(identity));
}

